import type { MarketingTranslator } from '@/i18n/marketing'
/**
 * Content for the LC Home/Main page (`/`).
 *
 * Layout, media and business values live here. Korean and English copy is
 * maintained in src/i18n/locales/{ko,en}/marketing.json.
 */

interface ReviewCardPhoto {
  id: number
  type: 'photo'
  title: string
  author?: string
  photo: string
}

interface ReviewCardText {
  id: number
  type: 'text'
  title: string
  content: string
  author: string
  backgroundColor: string
  avatar: string
}

export type ReviewCard = ReviewCardPhoto | ReviewCardText

export function getMainContent(mt: MarketingTranslator) {
  const mainContent: {
    hero: {
      title: string
      titleHighlight: string
      description: string
      emphasis: string
      ctaLabel: string
    }
    painPoints: {
      title: string
      titleHighlight: string
      body: string
      emphasis: string
    }
    problem: {
      title: string
      titleHighlight: string
      body: string
      emphasis: string
      carouselCaption: string
      images: string[]
    }
    animationFeature: {
      titleHighlight: string
      title: string
      body: string
      emphasis: string
      /** Number of placeholder thumbnails per vertical column (3 columns) */
      columnItemCount: number
    }
    sampleVideo: {
      titleHighlight: string
      title: string
      note: string
      posters: string[]
      videos: string[]
    }
    writeSpeak: {
      title: string
      titleHighlight: string
      body: string
      emphasis: string
    }
    reviews: {
      title: string
      titleHighlight: string
      ctaLabel: string
      cards: ReviewCard[]
    }
  } = {
    hero: {
      title: mt('main.mainContent.hero.title'),
      titleHighlight: mt('main.mainContent.hero.titleHighlight'),
      description:
        mt('main.mainContent.hero.description'),
      emphasis: mt('main.mainContent.hero.emphasis'),
      ctaLabel: mt('main.mainContent.hero.ctaLabel'),
    },

    painPoints: {
      title: mt('main.mainContent.painPoints.title'),
      titleHighlight: mt('main.mainContent.painPoints.titleHighlight'),
      body: mt('main.mainContent.painPoints.body'),
      emphasis: mt('main.mainContent.painPoints.emphasis'),
    },

    problem: {
      titleHighlight: mt('main.mainContent.problem.titleHighlight'),
      title: mt('main.mainContent.problem.title'),
      body: mt('main.mainContent.problem.body'),
      emphasis: mt('main.mainContent.problem.emphasis'),
      carouselCaption: mt('main.mainContent.problem.carouselCaption'),
      images: [
        '/marketing/main/docu-01.webp',
        '/marketing/main/docu-02.webp',
        '/marketing/main/docu-03.webp',
        '/marketing/main/docu-04.webp',
        '/marketing/main/docu-05.webp',
      ],
    },

    animationFeature: {
      titleHighlight: mt('main.mainContent.animationFeature.titleHighlight'),
      title: mt('main.mainContent.animationFeature.title'),
      body: mt('main.mainContent.animationFeature.body'),
      emphasis: mt('main.mainContent.animationFeature.emphasis'),
      columnItemCount: 6,
    },

    sampleVideo: {
      titleHighlight: mt('main.mainContent.sampleVideo.titleHighlight'),
      title: mt('main.mainContent.sampleVideo.title'),
      note: mt('main.mainContent.sampleVideo.note'),
      posters: [
        '/marketing/main/queture01.webp',
        '/marketing/main/queture02.webp',
        '/marketing/main/queture03.webp',
      ],
      videos: [
        'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/sample-queture-3/sample-queture-3.m3u8',
        'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/sample-queture-2/sample-queture-2.m3u8',
        'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/sample-queture-1/sample-queture-1.m3u8',
      ],
    },

    writeSpeak: {
      title: mt('main.mainContent.writeSpeak.title'),
      titleHighlight: mt('main.mainContent.writeSpeak.titleHighlight'),
      body: mt('main.mainContent.writeSpeak.body'),
      emphasis: mt('main.mainContent.writeSpeak.emphasis'),
    },

    reviews: {
      title: mt('main.mainContent.reviews.title'),
      titleHighlight: mt('main.mainContent.reviews.titleHighlight'),
      ctaLabel: mt('main.mainContent.reviews.ctaLabel'),
      cards: [
        {
          id: 11,
          type: 'photo',
          title: mt('main.mainContent.reviews.cards.0.title'),
          photo: '/marketing/main/img-photo06.webp',
        },
        {
          id: 12,
          type: 'text',
          title: mt('main.mainContent.reviews.cards.1.title'),
          content:
            mt('main.mainContent.reviews.cards.1.content'),
          author: mt('main.mainContent.reviews.cards.1.author'),
          backgroundColor: '#FFB828',
          avatar: '/marketing/main/img-avatar-6.svg',
        },
        {
          id: 13,
          type: 'text',
          title: mt('main.mainContent.reviews.cards.2.title'),
          content:
            mt('main.mainContent.reviews.cards.2.content'),
          author: mt('main.mainContent.reviews.cards.1.author'),
          backgroundColor: '#4599FF',
          avatar: '/marketing/main/img-avatar-7.svg',
        },
        {
          id: 14,
          type: 'photo',
          title: mt('main.mainContent.reviews.cards.3.title'),
          photo: '/marketing/main/img-photo07.webp',
        },
        {
          id: 15,
          type: 'text',
          title: mt('main.mainContent.reviews.cards.4.title'),
          content:
            mt('main.mainContent.reviews.cards.4.content'),
          author: mt('main.mainContent.reviews.cards.1.author'),
          backgroundColor: '#2AC47E',
          avatar: '/marketing/main/img-avatar-8.svg',
        },
        {
          id: 16,
          type: 'photo',
          title: mt('main.mainContent.reviews.cards.5.title'),
          photo: '/marketing/main/img-photo08.webp',
        },
        {
          id: 17,
          type: 'text',
          title: mt('main.mainContent.reviews.cards.6.title'),
          content:
            mt('main.mainContent.reviews.cards.6.content'),
          author: mt('main.mainContent.reviews.cards.6.author'),
          backgroundColor: '#FF675E',
          avatar: '/marketing/main/img-avatar-9.svg',
        },
        {
          id: 1,
          type: 'photo',
          title: mt('main.mainContent.reviews.cards.7.title'),
          author: mt('main.mainContent.reviews.cards.7.author'),
          photo: '/marketing/main/img-photo01.webp',
        },
        {
          id: 2,
          type: 'text',
          title: mt('main.mainContent.reviews.cards.8.title'),
          content:
            mt('main.mainContent.reviews.cards.8.content'),
          author: mt('main.mainContent.reviews.cards.8.author'),
          backgroundColor: '#FFB828',
          avatar: '/marketing/main/img-avatar-1.svg',
        },
        {
          id: 3,
          type: 'photo',
          title: mt('main.mainContent.reviews.cards.9.title'),
          author: mt('main.mainContent.reviews.cards.9.author'),
          photo: '/marketing/main/img-photo02.webp',
        },
        {
          id: 4,
          type: 'text',
          title:
            mt('main.mainContent.reviews.cards.10.title'),
          content:
            mt('main.mainContent.reviews.cards.10.content'),
          author: mt('main.mainContent.reviews.cards.10.author'),
          backgroundColor: '#4599FF',
          avatar: '/marketing/main/img-avatar-2.svg',
        },
        {
          id: 5,
          type: 'text',
          title: mt('main.mainContent.reviews.cards.11.title'),
          content:
            mt('main.mainContent.reviews.cards.11.content'),
          author: mt('main.mainContent.reviews.cards.11.author'),
          backgroundColor: '#2AC47E',
          avatar: '/marketing/main/img-avatar-3.svg',
        },
        {
          id: 6,
          type: 'photo',
          title: mt('main.mainContent.reviews.cards.12.title'),
          author: mt('main.mainContent.reviews.cards.12.author'),
          photo: '/marketing/main/img-photo03.webp',
        },
        {
          id: 7,
          type: 'text',
          title: mt('main.mainContent.reviews.cards.13.title'),
          content:
            mt('main.mainContent.reviews.cards.13.content'),
          author: mt('main.mainContent.reviews.cards.13.author'),
          backgroundColor: '#FF675E',
          avatar: '/marketing/main/img-avatar-4.svg',
        },
        {
          id: 8,
          type: 'photo',
          title: mt('main.mainContent.reviews.cards.14.title'),
          author: mt('main.mainContent.reviews.cards.14.author'),
          photo: '/marketing/main/img-photo04.webp',
        },
        {
          id: 9,
          type: 'photo',
          title: mt('main.mainContent.reviews.cards.15.title'),
          author: mt('main.mainContent.reviews.cards.15.author'),
          photo: '/marketing/main/img-photo05.webp',
        },
        {
          id: 10,
          type: 'text',
          title:
            mt('main.mainContent.reviews.cards.16.title'),
          content:
            mt('main.mainContent.reviews.cards.16.content'),
          author: mt('main.mainContent.reviews.cards.16.author'),
          backgroundColor: '#9C86E0',
          avatar: '/marketing/main/img-avatar-5.svg',
        },
      ],
    },
  }
  return mainContent
}
